import { Fragment, useEffect, useState } from 'react'
import { useSpring, animated } from '@react-spring/web'

import { getSpriteSize } from '~/components/Chest/helpers'

import { Breakpoint, useCheckBreakpoint } from '~/utils'

import ChestLoader from '~/components/Chest/components/ChestLoader'
import ChestSpriteImages from '~/components/Chest/components/ChestSpriteImages'

import { IMAGES_COUNT, SPRING_CONFIG, SPRITE_COLS, SPRITE_ROWS } from '../constants'

import ChecklistChestReward from './ChecklistChestReward'

interface Props {
  chestAnimation: boolean
  closeAnimation: () => void
}

export default function ChecklistCompleteChest({ chestAnimation, closeAnimation }: Props): React.ReactElement {
  const [frame, setFrame] = useState(1)
  const [spriteImageDidLoad, setSpriteImageDidLoad] = useState(false)
  const isInitialFrame = frame === 1
  const spring = useSpring(spriteImageDidLoad ? SPRING_CONFIG : {})

  const isDesktop = useCheckBreakpoint({ gt: Breakpoint.LG })

  const defaultSize = isDesktop ? 'xx-large' : 'medium-large'

  const [spriteWidth, spriteHeight] = getSpriteSize(defaultSize)
  const imageHeight = spriteHeight / SPRITE_ROWS
  const imageWidth = spriteWidth / SPRITE_COLS

  useEffect(() => {
    if (isInitialFrame && chestAnimation) {
      const interval = setInterval(() => {
        setFrame((prevState) => {
          if (prevState === IMAGES_COUNT) {
            clearInterval(interval)
            return prevState
          } else {
            return prevState + 1
          }
        })
      }, 50)
    }
  }, [isInitialFrame, chestAnimation])

  useEffect(() => {
    if (spriteImageDidLoad && frame === IMAGES_COUNT && chestAnimation) {
      setTimeout(() => {
        closeAnimation()
      }, 6000)
    }
  }, [frame, closeAnimation, spriteImageDidLoad, chestAnimation])

  return (
    <Fragment>
      <ChestLoader
        containerHeight={imageHeight}
        containerWidth={imageWidth}
        displayLoader={!spriteImageDidLoad}
        size={defaultSize}
      />
      <animated.div
        className={`relative ml-4 -mt-4 mb-4 ${spriteImageDidLoad ? 'block' : 'hidden'}`}
        data-test-id="reward-chest"
        style={{ minWidth: imageWidth, minHeight: imageHeight, ...spring }}
      >
        <ChestSpriteImages
          className="absolute max-w-none !m-0"
          frame={frame}
          imageHeight={imageHeight}
          imageWidth={imageWidth}
          setSpriteImageDidLoad={setSpriteImageDidLoad}
          spriteCols={SPRITE_COLS}
          spriteHeight={spriteHeight}
          spriteImageDidLoad={spriteImageDidLoad}
          spriteImgSrcKey="gamification/chests/ChecklistChest_spritesheet.png"
          spriteRows={SPRITE_ROWS}
          spriteWidth={spriteWidth}
        />
      </animated.div>
      {frame >= 21 && <ChecklistChestReward isDesktop={isDesktop} xp={2000} />}
    </Fragment>
  )
}
